// App.jsx - Main container component
import { useState, useEffect, useContext } from 'react';
import { Card, message, Input, Button } from 'antd';
import axios from 'axios';
import { LoginContext } from '../../../context/loginContext';
import InvoiceTypeSelector from './invoiceTypeSelector';
import InvoiceHeader from './invoiceHeader';
import TaxesAndRetentions from './taxesAndRetentions';
import PaymentTypesSelector from './paymentTypesSelector';
import MovementItemsTable from './movementItemsTable';
import TotalsSection from './totalSection';
import ActionButtons from './actionButtons';
import { Form } from 'antd';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Argentina/Buenos_Aires');

// Códigos AFIP de liquidaciones de granos que requieren CUIT/denominación
// de emisor o corredor en el archivo ARCA (Libro IVA Compras):
//   33  → Liquidación Primaria de Granos
//   331 → Liquidación Secundaria de Granos
const CODIGOS_CON_CORREDOR = [33, 331];

const extractCorredorFromObserva = (rawObserva) => {
  const raw = String(rawObserva || '');
  const cuitMatch = raw.match(/(?:cuit[_\s-]*corredor|cuit[_\s-]*emisor[_\s-]*corredor)\s*[:=]\s*([0-9.\-\s]+)/i);
  const denomMatch = raw.match(/(?:denominaci[oó]n[_\s-]*corredor|raz[oó]n[_\s-]*corredor|denominaci[oó]n[_\s-]*emisor[_\s-]*corredor)\s*[:=]\s*([^;|]+)/i);
  const cleaned = raw
    .replace(/(?:^|\s*\|\s*)cuit[_\s-]*corredor\s*[:=]\s*[0-9.\-\s]+/ig, '')
    .replace(/(?:^|\s*\|\s*)denominaci[oó]n[_\s-]*corredor\s*[:=]\s*[^|]+/ig, '')
    .replace(/\s*\|\s*\|\s*/g, ' | ')
    .trim();

  return {
    cuit: (cuitMatch?.[1] || '').replace(/\D/g, ''),
    denom: (denomMatch?.[1] || '').trim(),
    observations: cleaned
  };
};

const buildObservaWithCorredor = (observations, cuit, denom) => {
  const base = String(observations || '').trim();
  const cuitNorm = String(cuit || '').replace(/\D/g, '');
  const denomNorm = String(denom || '').trim();
  const parts = [base];

  if (cuitNorm) parts.push(`cuit_corredor=${cuitNorm}`);
  if (denomNorm) parts.push(`denominacion_corredor=${denomNorm}`);

  return parts.filter(Boolean).join(' | ');
};

const CNuevoMovimientoAdministrador = ({ idMovimiento, onCancel, onSuccess }) => {
  // Context and state
  const { user, selectedEmpresa } = useContext(LoginContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [invoiceType, setInvoiceType] = useState(1); // 1: Purchase, 2: Sales

  // Data lists
  const [accounts, setAccounts] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accountPlans, setAccountPlans] = useState([]);
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(0);
  const [movementItems, setMovementItems] = useState([
    { key: 0, amount: 0, type: 'INGRESO', costCenter: null }
  ]);

  // Paginación y filtrado para las cuentas
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 5;
  const [filters, setFilters] = useState({ cue_nombre: '', cue_cuit: '' });
  const [loadingMovimiento, setLoadingMovimiento] = useState(!!idMovimiento);
  const selectedDocumentTypeId = Form.useWatch('documentType', form);
  const selectedDocumentType = documentTypes.find(
    (tipo) => Number(tipo.tic_id) === Number(selectedDocumentTypeId)
  );
  const selectedDocumentCodigo = Number(selectedDocumentType?.tic_codigo);
  const showCorredorFields = CODIGOS_CON_CORREDOR.includes(selectedDocumentCodigo);

  // Load all required data on component mount
  useEffect(() => {
    fetchAccounts();
    fetchDocumentTypes();
    fetchPaymentTypes();
    fetchCostCenters();
    fetchAccountPlans();
    if (idMovimiento) {
      setLoadingMovimiento(true);
      fetchMovimientoData().finally(() => setLoadingMovimiento(false));
    }
  }, [currentPage, filters, idMovimiento]); // Agregamos currentPage y filters como dependencias

  const fetchMovimientoData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/movimiento/${idMovimiento}`, config);

      const formatDate = (dateString) => {
        if (!dateString) return null;
        return dayjs.utc(dateString).tz('America/Argentina/Buenos_Aires', true);
      };


      const movimiento = response.data;
      const corredorData = extractCorredorFromObserva(movimiento.mov_observa || '');
      const cuitCorredor = String(movimiento.mov_cuit_corredor || '').replace(/\D/g, '') || corredorData.cuit;
      const denomCorredor = String(movimiento.mov_denom_corredor || '').trim() || corredorData.denom;
      setSelectedAccount(movimiento.cue_id);
      // El Radio usa estado local; sin esto, al editar ventas seguía enviándose mov_libroIVA=1 (compras).
      const libro = Number(movimiento.mov_libroIVA);
      if (libro === 1 || libro === 2) setInvoiceType(libro);
      // Configurar valores iniciales en el formulario con nombres consistentes
      form.setFieldsValue({
        account: movimiento.cue_nombre,
        documentType: movimiento.mov_tipocomprobante,
        pointOfSale: movimiento.mov_ptovta,
        documentNumber: movimiento.mov_nrocomp,
        originDate: formatDate(movimiento.mov_fecha),
        ivaDate: formatDate(movimiento.mov_fechaiva),
        gravada21: parseFloat(movimiento.mov_gravada21) || 0,
        gravada105: parseFloat(movimiento.mov_gravada105) || 0,
        gravada27: parseFloat(movimiento.mov_gravada27) || 0,
        iva21: parseFloat(movimiento.mov_iva21) || 0,
        iva105: parseFloat(movimiento.mov_iva105) || 0,
        iva27: parseFloat(movimiento.mov_iva27) || 0,
        percIva: parseFloat(movimiento.mov_piva) || 0,
        percIB: parseFloat(movimiento.mov_pib) || 0,
        exento: parseFloat(movimiento.mov_exento) || 0,
        retIva: parseFloat(movimiento.mov_retiva) || 0,
        retGan: parseFloat(movimiento.mov_retgan) || 0,
        retIIBB: parseFloat(movimiento.mov_retib) || 0,
        total: parseFloat(movimiento.mov_total) || 0,
        invoiceType: movimiento.mov_libroIVA,
        observations: corredorData.observations,
        emisorCorredorCuit: cuitCorredor || '',
        emisorCorredorDenom: denomCorredor || '',
        cantidad: movimiento.mov_cantidad,

        // Arreglos para FormList con nombres consistentes
        paymentTypes: movimiento.pagos?.map(pago => pago.pag_tipopago) || [],

        breakdowns: movimiento.desgloses?.map(desglose => ({
          id: desglose.des_id,
          costCenter: desglose.des_centro,
          accountPlan: desglose.des_plancuenta,
          amount: parseFloat(desglose.des_importe) || 0
        })) || [],

        dueDates: movimiento.vencimientos?.map(vencimiento => ({
          id: vencimiento.ven_id,
          amount: parseFloat(vencimiento.ven_importe) || 0,
          date: formatDate(vencimiento.ven_fecha)
        })) || []
      });

      // Actualizar el estado selectedPaymentTypes con los pagos del movimiento
      setSelectedPaymentTypes(movimiento.pagos || []);

      // Actualizar el estado movementItems con los desgloses del movimiento
      if (movimiento.desgloses && movimiento.desgloses.length > 0) {
        const formattedDesgloses = movimiento.desgloses.map((desglose, index) => ({
          key: index,
          amount: parseFloat(desglose.des_importe) || 0,
          planCuentas: parseInt(desglose.des_plancuenta),
          costCenter: desglose.des_centro,
          id: desglose.des_id
        }));
        setMovementItems(formattedDesgloses);
      }
      console.log(movimiento)
      return movimiento;
    } catch (error) {
      console.error('Error obteniendo datos del movimiento:', error);
      message.error('Error al cargar los datos del movimiento');
      throw error;
    }
  };
  // API calls to fetch data
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        ...(filters.cue_cuit && { cue_cuit: filters.cue_cuit }),
        ...(filters.cue_nombre && { cue_nombre: filters.cue_nombre }),
      });

      const url = `${process.env.REACT_APP_API_URL}/cuenta/empresa/${selectedEmpresa.emp_id}?${queryParams.toString()}`;
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const response = await axios.get(url, config);

      setAccounts(response.data.cuentas);
      setTotalItems(response.data.total);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setAccounts([]); // Limpiar cuentas si no se encontraron
        setTotalItems(0);
        message.info('No se encontraron cuentas para esta empresa');
      } else {
        console.error('Error fetching accounts:', error);
        message.error('Error al cargar las cuentas');
      }
    } finally {
      setLoading(false);
    }
  };


  const fetchDocumentTypes = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/tipos-comprobantes`;
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      const response = await axios.get(url, config);
      setDocumentTypes(response.data);
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/tipo-pago`;
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      const response = await axios.get(url, config);
      setPaymentTypes(response.data);
    } catch (error) {
      console.error('Error fetching payment types:', error);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/centro-costo/${selectedEmpresa.emp_id}`;
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      const response = await axios.get(url, config);
      setCostCenters(response.data);
    } catch (error) {
      console.error('Error fetching cost centers:', error);
    }
  };

  const fetchAccountPlans = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/plan-cuenta/${selectedEmpresa.emp_id}`;
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      const response = await axios.get(url, config);
      setAccountPlans(response.data);
    } catch (error) {
      console.error('Error fetching account plans:', error);
    }
  };

  // Calculate totals for display and submission
  const toFloat = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const calculateTotals = (values) => {
    const gravada21 = toFloat(values.gravada21);
    const gravada105 = toFloat(values.gravada105);
    const gravada27 = toFloat(values.gravada27);
    const exento = toFloat(values.exento);

    const iva21 = toFloat(values.iva21);
    const iva105 = toFloat(values.iva105);
    const iva27 = toFloat(values.iva27);

    const retIva = toFloat(values.retIva);
    const retGan = toFloat(values.retGan);
    const retIIBB = toFloat(values.retIIBB);

    const subtotal = gravada21 + gravada105 + gravada27 + exento;
    const totalIva = iva21 + iva105 + iva27;
    const totalRetentions = retIva + retGan + retIIBB;

    const total = subtotal + totalIva - totalRetentions;

    return {
      subtotal,
      totalIva,
      totalRetentions,
      total
    };
  };


  const padNumber = (value, length) => {
    if (!value) return '';
    console.log(value.toString().padStart(length, '0'));
    return value.toString().padStart(length, '0');
  };

  // Form submission
  // Form submission with validations
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const totals = calculateTotals(values);
      const movCuenta = Number(selectedAccount);
      const selectedDocumentType = documentTypes.find((tipo) => Number(tipo.tic_id) === Number(values.documentType));
      const codigoSeleccionado = Number(selectedDocumentType?.tic_codigo);
      const requiereCorredor = CODIGOS_CON_CORREDOR.includes(codigoSeleccionado);

      // Validation 1: Must select an account
      if (!movCuenta) {
        message.warning("Debe seleccionar una cuenta antes de continuar.");
        setLoading(false);
        return;
      }

      // Validation 2: Movement items are required
      if (!movementItems || movementItems.length === 0) {
        message.warning("Debe ingresar al menos un detalle de movimiento.");
        setLoading(false);
        return;
      }

      const invalidItems = movementItems.filter(
        (item) => !item.costCenter || !item.planCuentas || item.amount === undefined || item.amount === null
      );

      if (invalidItems.length > 0) {
        message.warning("Cada detalle de movimiento debe incluir un Centro de Costo, Plan de Cuentas y un Monto válido.");
        setLoading(false);
        return;
      }

      // Validation 3: Check if movement items sum equals subtotal
      const movementItemsTotal = movementItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const subtotal = totals.subtotal;

      if (Math.abs(movementItemsTotal - subtotal) > 0.01) { // Allow small rounding differences
        message.warning(`La suma de los montos en el detalle de movimientos (${movementItemsTotal.toFixed(2)}) debe ser igual al subtotal (${subtotal.toFixed(2)}).`);
        setLoading(false);
        return;
      }

      // Validation 4: Check if payment types sum equals total (only if payment types exist)
      if (selectedPaymentTypes && selectedPaymentTypes.length > 0) {
        const paymentTypesTotal = selectedPaymentTypes.reduce((sum, pt) => sum + Number(pt.pag_importe || 0), 0);

        if (Math.abs(paymentTypesTotal - totals.total) > 0.01) { // Allow small rounding differences
          message.warning(`La suma de los montos de tipos de pago (${paymentTypesTotal.toFixed(2)}) debe ser igual al total del comprobante (${totals.total.toFixed(2)}).`);
          setLoading(false);
          return;
        }

        const invalidPayments = selectedPaymentTypes.filter(pt => !pt.pag_fechaorigen);
        if (invalidPayments.length > 0) {
          message.warning("Todos los tipos de pago deben tener una fecha de origen válida.");
          setLoading(false);
          return;
        }
      }
      const libroIva = Number(values.invoiceType ?? invoiceType);
      const cuitCorredorNorm = String(values.emisorCorredorCuit || '').replace(/\D/g, '');
      const codigoLabel = codigoSeleccionado === 331 ? '331' : '033';
      if (libroIva === 1 && requiereCorredor && cuitCorredorNorm.length !== 11) {
        message.warning(`Para comprobante ${codigoLabel} debe ingresar CUIT de emisor/corredor (11 dígitos).`);
        setLoading(false);
        return;
      }
      if (libroIva === 1 && requiereCorredor && !String(values.emisorCorredorDenom || '').trim()) {
        message.warning(`Para comprobante ${codigoLabel} debe ingresar la denominación del emisor/corredor.`);
        setLoading(false);
        return;
      }
      console.log(values.ivaDate)
      const movimientoData = {
        mov_cuenta: Number(selectedAccount),
        mov_tipocomprobante: Number(values.documentType),
        mov_ptovta: padNumber(values.pointOfSale.toString(), 5),
        mov_nrocomp: padNumber(values.documentNumber.toString(), 8),
        mov_fecha: values.originDate.format('YYYY-MM-DD HH:mm:ss'),
        mov_fechaiva: values.ivaDate ? values.ivaDate.format('YYYY-MM-DD HH:mm:ss') : null,
        mov_gravada21: Number(values.gravada21) || 0,
        mov_gravada105: Number(values.gravada105) || 0,
        mov_gravada27: Number(values.gravada27) || 0,
        mov_iva21: Number(values.iva21) || 0,
        mov_iva105: Number(values.iva105) || 0,
        mov_iva27: Number(values.iva27) || 0,
        mov_piva: Number(values.percIva) || 0,
        mov_pib: Number(values.percIB) || 0,
        mov_exento: Number(values.exento) || 0,
        mov_nogravada: 0,
        mov_retiva: Number(values.retIva) || 0,
        mov_retgan: Number(values.retGan) || 0,
        mov_retib: Number(values.retIIBB) || 0,
        mov_iinterno: 0,
        mov_total: Number(totals.total),
        mov_libroIVA: libroIva,
        mov_observa: buildObservaWithCorredor(
          values.observations,
          values.emisorCorredorCuit,
          values.emisorCorredorDenom
        ),
        mov_cuit_corredor: cuitCorredorNorm || null,
        mov_denom_corredor: String(values.emisorCorredorDenom || '').trim() || null,
        mov_cantidad: Number(values.cantidad || 0),
        emp_id: selectedEmpresa.emp_id,
        payment_types: selectedPaymentTypes.map(pt => ({
          pag_id: pt.pag_id, // Include if it exists (for updates)
          pag_tipopago: Number(pt.pag_tipopago),
          pag_importe: Number(pt.pag_importe),
          pag_banco: pt.pag_banco,
          pag_comprobante: pt.pag_comprobante,
          pag_fechaorigen: pt.pag_fechaorigen ? moment(pt.pag_fechaorigen, "YYYY-MM-DD") : null,
          pag_fechavencimiento: pt.pag_fechavencimiento ? moment(pt.pag_fechavencimiento, "YYYY-MM-DD") : null,
          pag_acreditado: pt.pag_acreditado,
          pag_ingegr: libroIva
        })),
        movement_items: movementItems.map(item => ({
          des_centro: item.costCenter,
          des_plancuenta: item.planCuentas,
          des_importe: Number(item.amount),
          des_id: item.id ? Number(item.id) : undefined
        })),
        vencimientos: values.dueDates && values.dueDates.length > 0
          ? values.dueDates.map(v => ({
            fecha: dayjs(v.date).format('YYYY-MM-DD'),
            importe: Number(v.amount) || 0
          }))
          : [], // Si no hay vencimientos, enviar un array vacío
        ...(idMovimiento && { mov_id: Number(idMovimiento) })
      };

      if (idMovimiento) {
        movimientoData.payment_types = selectedPaymentTypes
      }

      console.log('FORMULARIO', movimientoData);
      const url = `${process.env.REACT_APP_API_URL}/movimiento${idMovimiento ? `/${idMovimiento}` : ''}`;
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };

      // Si es una actualización, usar PUT en lugar de POST
      const method = idMovimiento ? 'put' : 'post';
      await axios[method](url, movimientoData, config);

      // Reset form and state
      form.resetFields();
      setSelectedPaymentTypes([]);
      setMovementItems([{ key: 0, amount: 0, type: 'INGRESO', costCenter: null, planCuentas: null }]);

      // Success message
      message.success(libroIva === 1 ? 'Factura de compra guardada correctamente' : 'Factura de venta guardada correctamente');

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving invoice:', error);

      // Verificar si es un error 409 (Conflicto por movimiento duplicado)
      if (error.response && error.response.status === 409) {
        // Mostrar mensaje específico para el caso de movimiento duplicado
        const errorMessage = error.response.data.error || 'Ya existe un movimiento con estos datos.';
        message.error(
          <div>
            <p><strong>Error: Movimiento duplicado</strong></p>
            <p>{errorMessage}</p>
            <p>No se puede crear un movimiento con el mismo punto de venta y número de comprobante.</p>
          </div>,
          6 // Duración más larga (6 segundos) para que el usuario pueda leer el mensaje
        );
      } else {
        // Para otros errores, mostrar el mensaje genérico
        message.error('Error al guardar la factura');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    const { gravada21, gravada105, gravada27 } = allValues;

    form.setFieldsValue({
      iva21: gravada21 ? parseFloat((gravada21 * 0.21).toFixed(2)) : 0,
      iva105: gravada105 ? parseFloat((gravada105 * 0.105).toFixed(2)) : 0,
      iva27: gravada27 ? parseFloat((gravada27 * 0.27).toFixed(2)) : 0,
    });
  };

  return (

    <Card title="Alta de Movimientos">
      {idMovimiento && (
        <Button type="primary" onClick={onCancel} style={{ marginBottom: '10px' }}>
          Volver a la lista
        </Button>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={handleValuesChange}
        initialValues={{
        }}
        disabled={loadingMovimiento}
      >
        <InvoiceTypeSelector
          invoiceType={invoiceType}
          setInvoiceType={setInvoiceType}
        />

        <InvoiceHeader
          accounts={accounts}
          documentTypes={documentTypes}
          showCorredorFields={showCorredorFields}
          fetchAccounts={fetchAccounts}
          totalItems={totalItems}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={loading}
          pageSize={pageSize}
          filters={filters}
          setFilters={setFilters}
          setSelectedAccount={setSelectedAccount}
        />

        <TaxesAndRetentions
          form={form}
          handleValuesChange={handleValuesChange} />

        <PaymentTypesSelector
          paymentTypes={paymentTypes}
          selectedPaymentTypes={selectedPaymentTypes}
          setSelectedPaymentTypes={setSelectedPaymentTypes}
          user={user}
        />

        <MovementItemsTable
          movementItems={movementItems}
          setMovementItems={setMovementItems}
          costCenters={costCenters}
          planCuentas={accountPlans}
        />

        <TotalsSection
          form={form}
          calculateTotals={calculateTotals}
        />

        <ActionButtons
          loading={loading}
          form={form}
        />
      </Form>
    </Card>
  );
};

export default CNuevoMovimientoAdministrador;